import {DEFAULT_DIAGNOSTIC_REGIONS, getRegionForCode} from './regions'
import {measureLatencySample} from './latency'
import {
  getConnectInstanceLatencyEndpointUrl,
  getConnectInstanceUrlFromEnv,
  getRegionLatencyEndpointUrl,
  toConnectInstanceOrigin,
} from './urls'
import type {LatencySample} from './types'

export type ConnectHeartbeatProbeResult = {
  name: string
  code: string
  endpoint: string
  latencyMs: number
  success: boolean
  error: string | null
}

export type ConnectHeartbeatProbeReport = {
  instance: ConnectHeartbeatProbeResult | null
  region: ConnectHeartbeatProbeResult | null
  startedAt: number
  endedAt: number
}

const toProbeResult = (
  meta: {name: string; code: string},
  endpoint: string,
  sample: LatencySample
): ConnectHeartbeatProbeResult => ({
  name: meta.name,
  code: meta.code,
  endpoint,
  latencyMs: sample.latency,
  success: sample.success,
  error: sample.error,
})

/**
 * Lightweight Connect latency probe for the continuous telemetry loop.
 * One HTTP sample each for instance config.json + region telemetry /ping.
 * No warmup, no media STUN, no client-config snapshot, no 60s cooldown.
 */
export const probeConnectHeartbeat = async (options?: {
  connectInstanceUrl?: string
  regionCode?: string
}): Promise<ConnectHeartbeatProbeReport> => {
  const startedAt = Date.now()
  const connectInstanceUrl = toConnectInstanceOrigin(
    options?.connectInstanceUrl || getConnectInstanceUrlFromEnv()
  )
  const regionCode = options?.regionCode || DEFAULT_DIAGNOSTIC_REGIONS[0]

  let instance: ConnectHeartbeatProbeResult | null = null
  if (connectInstanceUrl) {
    const endpoint = getConnectInstanceLatencyEndpointUrl(connectInstanceUrl)
    const sample = await measureLatencySample(endpoint)
    instance = toProbeResult({name: connectInstanceUrl, code: ''}, endpoint, sample)
  }

  let region: ConnectHeartbeatProbeResult | null = null
  const regionConfig = getRegionForCode(regionCode)
  if (regionConfig) {
    const endpoint = getRegionLatencyEndpointUrl(regionConfig.latencyEndpoint)
    const sample = await measureLatencySample(endpoint)
    region = toProbeResult(
      {name: `${regionConfig.name} (${regionConfig.code})`, code: regionConfig.code},
      endpoint,
      sample
    )
  }

  return {
    instance,
    region,
    startedAt,
    endedAt: Date.now(),
  }
}
